import { z } from 'zod'
import type { ApiClient } from './client'

/**
 * Cases — the LEGACY endpoints the console still reads. Case work itself
 * moved to case management V2 (case-mgmt.ts); two reads stay here because
 * the signals side has no V2 counterpart yet:
 *
 *     POST /{clusterId}/api/cases/stats       legacy "signal statistics" (overview posture, Signals stats band)
 *     POST /{clusterId}/api/cases/get_graph   legacy cases mapped to connected-signals subgraphs, by name
 *
 * Envelope `{ TStamp, count?, data, message, status }` (VERIFIED 2026-08-24,
 * docs/inventory/cases-write-capture.json). The legacy case ids these return
 * open through V2's legacy-id lookup.
 */

export type CaseRow = Record<string, unknown>

/* ---------------------------------------------------------------- posture */

export interface PostureCounter {
  count: number
  /** Percent change vs the preceding window; null when the server omits it. */
  percentDiff: number | null
}

export interface PostureTopEntry {
  name: string
  count: number
}

/**
 * `POST /api/cases/stats` — the legacy "signal statistics" endpoint behind
 * the old home + signal-analyzer headers, DISTINCT from `case_stats`.
 * Normalized view for the Vantage overview.
 */
export interface PostureStats {
  cases: PostureCounter
  signals: PostureCounter
  detections: PostureCounter
  suspects: PostureCounter
  targets: PostureCounter
  /** Mean time to detect, SECONDS (live float). */
  mttdSeconds: number | null
  severity: Array<{ severity: string; value: number }>
  /** Stream names joined with ' + ' when the wire sends an array per row. */
  streams: Array<{ stream: string; value: number }>
  /** Every stream name in `streams`, one each (an array row split back into its names), sorted. */
  streamNames: string[]
  topDetections: PostureTopEntry[]
  topSources: PostureTopEntry[]
  topSuspects: PostureTopEntry[]
  topTargets: PostureTopEntry[]
  topTactics: PostureTopEntry[]
  topTechniques: PostureTopEntry[]
}

const finiteOrNull = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)

const looseObject = z.record(z.string(), z.unknown()).default({})
const looseRows = z.array(z.record(z.string(), z.unknown())).default([])
const topSection = z.object({ stats: looseRows }).passthrough().default({})

/**
 * cases/get_graph — `data` maps each asked subgraph name that has cases to
 * its case rows (`case_mgmt` rows, or `{case_id}` alone when the case is
 * gone). Read from the handler 2026-10-07, UNVERIFIED live. `data: {}` is
 * "no name has a case"; a non-success status or a missing / non-object
 * `data` is a failure (`readCaseGraph` throws), never "no case".
 */
export const caseGraphResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().nullable().optional().catch(null),
    data: z.record(z.string(), looseRows.catch([])).nullable().catch(null).default(null),
  })
  .passthrough()

/** A refused or empty-handed cases/get_graph. */
export class CaseGraphError extends Error {}

export function readCaseGraph(res: z.output<typeof caseGraphResponse>): Record<string, CaseRow[]> {
  if (res.status !== undefined && res.status !== 'success') throw new CaseGraphError(res.message || 'Mapped cases could not be read.')
  if (res.data === null) throw new CaseGraphError(res.message || 'Mapped cases returned no result.')
  return res.data as Record<string, CaseRow[]>
}

function readPostureCounter(o: Record<string, unknown>, key: 'new' | 'unique'): PostureCounter {
  return { count: finiteOrNull(o[key]) ?? 0, percentDiff: finiteOrNull(o.percent_diff) }
}

function readPostureTop(rows: Array<Record<string, unknown>>, field: string): PostureTopEntry[] {
  const out: PostureTopEntry[] = []
  for (const r of rows) {
    const raw = r[field]
    const name = typeof raw === 'string' ? raw : typeof raw === 'number' ? String(raw) : ''
    if (name === '') continue
    out.push({ name, count: finiteOrNull(r.count) ?? 0 })
  }
  return out
}

/**
 * VERIFIED response shape (docs/inventory/signals-capture.json, result
 * "cases/stats"): data carries new/unique counters with `percent_diff`,
 * `mttd` (float seconds), `severity[]`, `stream[]` (stream is an ARRAY of
 * names per row), and `top_*.stats[]` keyed by per-list field names
 * (`detectionname`, `workbook`, `suspect`, `target`, `detectiontactic`,
 * `detectiontechnique`). Every section is defaulted so an empty scope
 * renders zeros instead of crashing.
 */
export const postureStatsResponse = z
  .object({
    status: z.string().optional(),
    message: z.string().optional(),
    data: z
      .object({
        cases: looseObject,
        signals: looseObject,
        detections: looseObject,
        suspects: looseObject,
        targets: looseObject,
        mttd: z.unknown().optional(),
        severity: looseRows,
        stream: looseRows,
        top_detections: topSection,
        top_sources: topSection,
        top_suspects: topSection,
        top_targets: topSection,
        top_tactics: topSection,
        top_techniques: topSection,
      })
      .passthrough()
      .default({}),
  })
  .transform((r): PostureStats => {
    const d = r.data
    return {
      cases: readPostureCounter(d.cases, 'new'),
      signals: readPostureCounter(d.signals, 'new'),
      detections: readPostureCounter(d.detections, 'unique'),
      suspects: readPostureCounter(d.suspects, 'unique'),
      targets: readPostureCounter(d.targets, 'unique'),
      mttdSeconds: finiteOrNull(d.mttd),
      severity: d.severity
        .map((s) => ({ severity: typeof s.severity === 'string' ? s.severity : '', value: finiteOrNull(s.value) ?? 0 }))
        .filter((s) => s.severity !== ''),
      streams: d.stream
        .map((s) => ({
          stream: Array.isArray(s.stream) ? s.stream.map(String).join(' + ') : typeof s.stream === 'string' ? s.stream : '',
          value: finiteOrNull(s.value) ?? 0,
        }))
        .filter((s) => s.stream !== ''),
      streamNames: [
        ...new Set(d.stream.flatMap((s) => (Array.isArray(s.stream) ? s.stream.map(String) : typeof s.stream === 'string' ? [s.stream] : []))),
      ]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
      topDetections: readPostureTop(d.top_detections.stats, 'detectionname'),
      topSources: readPostureTop(d.top_sources.stats, 'workbook'),
      topSuspects: readPostureTop(d.top_suspects.stats, 'suspect'),
      topTargets: readPostureTop(d.top_targets.stats, 'target'),
      topTactics: readPostureTop(d.top_tactics.stats, 'detectiontactic'),
      topTechniques: readPostureTop(d.top_techniques.stats, 'detectiontechnique'),
    }
  })

export interface CaseScope {
  clusterId: string
  scopeId: string
}

export interface CasesApi {
  /** Legacy cases mapped to each named subgraph — full case rows keyed by name; names with none are absent. */
  getGraph: (q: CaseScope & { names: string[] }) => Promise<Record<string, CaseRow[]>>
  /**
   * The legacy "signal statistics" endpoint (`cases/stats`): posture
   * counters + top lists for the Vantage overview and the Signals stats
   * band. Body per the openapi example: scope_id, duration, start/end,
   * customer_admin.
   */
  postureStats: (q: CaseScope & { duration?: string; startTime?: string; endTime?: string }) => Promise<PostureStats>
}

export function createCasesApi(client: ApiClient): CasesApi {
  return {
    getGraph: async ({ clusterId, scopeId, names }) =>
      readCaseGraph(
        await client.request('POST', `/${clusterId}/api/cases/get_graph`, {
          body: { scope_id: scopeId, graph_name: names },
          schema: caseGraphResponse,
        }),
      ),

    postureStats: async ({ clusterId, scopeId, duration = '7d', startTime, endTime }) =>
      client.request('POST', `/${clusterId}/api/cases/stats`, {
        body: { scope_id: scopeId, duration, start_time: startTime ?? '', end_time: endTime ?? '', customer_admin: true },
        schema: postureStatsResponse,
      }),
  }
}
